import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "sonner";

import Header from "./Header.tsx";
import Sidebar from "./Sidebar.tsx";
import styles from "./PumpDashboard.module.css";
import useTheme from "../hooks/useTheme.ts";

interface ReengagementEvent {
  fleet: number;
  pump: string;
  alarm: string;
  graph_channel?: string | null;
  alarm_time: string | null;
  alarm_time_ms: number;
  source_alarm_time?: string | null;
  source_alarm_time_ms?: number | null;
  alarm_time_adjusted?: boolean;
  trip_trigger_discharge_pressure?: number | null;
  trip_trigger_trip_pressure?: number | null;
  neutral_time_ms: number;
  reengagement_time_ms: number;
  time_to_reengage_seconds: number;
  gear: number;
  alarm_rpm?: number | null;
  rpm?: number | null;
  discharge_pressure: number;
}

interface ActiveStage {
  fleet?: number | string | null;
  fleet_id?: number | string | null;
}

// Large-range loading is intentionally time-bounded, not source-bounded.
// The old planner produced thousands of per-source XHRs for a month of history.
// This version uses fixed six-hour blocks, at most four requests at a time, and
// lets SQL/backend own request duration so the browser never kills a valid query
// at the old 45/120 second timeout.
const REQUEST_TIMEOUT_MS = 0;
const RANGE_CHUNK_MS = 6 * 60 * 60 * 1000;
const MIN_SPLIT_RANGE_MS = 30 * 60 * 1000;
const MAX_RANGE_CONCURRENT_REQUESTS = 4;
const MAX_TRANSIENT_RETRIES = 2;
const MAX_RATE_LIMIT_RETRIES = 5;
const RETRY_BASE_DELAY_MS = 1500;
const PROGRESS_UPDATE_INTERVAL_MS = 500;

const CHART_MINUTES_BEFORE_ALARM = 10;
const CHART_MINUTES_AFTER_REENGAGEMENT = 10;

const FlaggedReengagementEvents: React.FC = () => {
  const { theme } = useTheme();

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";

  const [showSidebar, setShowSidebar] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true,
  );

  const getTodayStart = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const [fromDate, setFromDate] = useState<Date>(getTodayStart);
  const [toDate, setToDate] = useState<Date>(new Date());

  const [fleetFilter, setFleetFilter] = useState("");
  const [pumpFilter, setPumpFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [minimumRpmFilter, setMinimumRpmFilter] = useState("");

  const [events, setEvents] = useState<ReengagementEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState("");
  const [loadFailureMessage, setLoadFailureMessage] = useState("");
  const [totalEventCount, setTotalEventCount] = useState<number | null>(null);

  const activeLoadControllerRef = useRef<AbortController | null>(null);
  const loadGenerationRef = useRef(0);

  const getAuthToken = () => {
    return localStorage.getItem("jwt") || localStorage.getItem("token");
  };

  const getRequestConfig = () => {
    const token = getAuthToken();

    if (!token) {
      return null;
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      timeout: REQUEST_TIMEOUT_MS,
    };
  };

  const loadEvents = useCallback(async () => {
    if (!fromDate || !toDate) {
      toast.warning("Please select a date range");
      return;
    }

    if (toDate.getTime() <= fromDate.getTime()) {
      toast.warning("End date must be after start date");
      return;
    }

    const minimumRpmText = minimumRpmFilter.trim();
    const minimumRpm = minimumRpmText === "" ? null : Number(minimumRpmText);

    if (
      minimumRpm !== null &&
      (!Number.isFinite(minimumRpm) || minimumRpm < 0)
    ) {
      toast.warning("Minimum RPM must be a number greater than or equal to 0");
      return;
    }

    const requestConfig = getRequestConfig();

    if (!requestConfig) {
      toast.error("Authentication token missing");
      setEvents([]);
      return;
    }

    activeLoadControllerRef.current?.abort();

    const controller = new AbortController();
    activeLoadControllerRef.current = controller;

    const loadGeneration = loadGenerationRef.current + 1;
    loadGenerationRef.current = loadGeneration;

    const isCurrentLoad = () =>
      !controller.signal.aborted &&
      loadGenerationRef.current === loadGeneration;

    const normalizeEvent = (
      rawEvent: ReengagementEvent,
    ): ReengagementEvent => ({
      ...rawEvent,
      fleet: Number(rawEvent.fleet) || 0,
      pump: String(rawEvent.pump ?? ""),
      alarm: String(rawEvent.alarm ?? ""),
      graph_channel: rawEvent.graph_channel
        ? String(rawEvent.graph_channel)
        : null,
      alarm_time_ms: Number(rawEvent.alarm_time_ms) || 0,
      source_alarm_time_ms:
        rawEvent.source_alarm_time_ms === null ||
        rawEvent.source_alarm_time_ms === undefined
          ? null
          : Number(rawEvent.source_alarm_time_ms),
      alarm_time_adjusted: Boolean(rawEvent.alarm_time_adjusted),
      trip_trigger_discharge_pressure:
        rawEvent.trip_trigger_discharge_pressure === null ||
        rawEvent.trip_trigger_discharge_pressure === undefined
          ? null
          : Number(rawEvent.trip_trigger_discharge_pressure),
      trip_trigger_trip_pressure:
        rawEvent.trip_trigger_trip_pressure === null ||
        rawEvent.trip_trigger_trip_pressure === undefined
          ? null
          : Number(rawEvent.trip_trigger_trip_pressure),
      neutral_time_ms: Number(rawEvent.neutral_time_ms) || 0,
      reengagement_time_ms: Number(rawEvent.reengagement_time_ms) || 0,
      time_to_reengage_seconds: Number(rawEvent.time_to_reengage_seconds) || 0,
      gear: Number(rawEvent.gear) || 0,
      alarm_rpm:
        rawEvent.alarm_rpm === null || rawEvent.alarm_rpm === undefined
          ? null
          : Number(rawEvent.alarm_rpm),
      rpm:
        rawEvent.rpm === null || rawEvent.rpm === undefined
          ? null
          : Number(rawEvent.rpm),
      discharge_pressure: Number(rawEvent.discharge_pressure) || 0,
    });

    /*
     * De-duplicate by the physical neutral/re-engagement sequence instead of
     * by alarm text. Two candidate alarms can sit on opposite sides of a chunk
     * boundary and still point to the same physical event. Keep the qualifying
     * alarm nearest to neutral, matching the backend SQL rule.
     */
    const normalizeAndDedupeEvents = (
      rawEvents: ReengagementEvent[],
    ): ReengagementEvent[] => {
      const selected = new Map<string, ReengagementEvent>();

      rawEvents.map(normalizeEvent).forEach((event) => {
        const key = [
          event.fleet,
          event.pump,
          event.neutral_time_ms,
          event.reengagement_time_ms,
        ].join("-");

        const existing = selected.get(key);

        if (!existing) {
          selected.set(key, event);
          return;
        }

        const candidateGap =
          event.alarm_time_ms > 0 &&
          event.alarm_time_ms <= event.neutral_time_ms
            ? event.neutral_time_ms - event.alarm_time_ms
            : Number.MAX_SAFE_INTEGER;

        const existingGap =
          existing.alarm_time_ms > 0 &&
          existing.alarm_time_ms <= existing.neutral_time_ms
            ? existing.neutral_time_ms - existing.alarm_time_ms
            : Number.MAX_SAFE_INTEGER;

        if (
          candidateGap < existingGap ||
          (candidateGap === existingGap &&
            event.alarm_time_ms > existing.alarm_time_ms)
        ) {
          selected.set(key, event);
        }
      });

      return Array.from(selected.values()).sort(
        (left, right) =>
          Number(right.reengagement_time_ms) -
          Number(left.reengagement_time_ms),
      );
    };

    const isTransientRangeError = (error: unknown) => {
      if (!axios.isAxiosError(error)) return false;

      const axiosError = error as any;
      const status = axiosError.response?.status;

      return (
        !axiosError.response ||
        axiosError.code === "ECONNABORTED" ||
        axiosError.code === "ETIMEDOUT" ||
        status === 408 ||
        status === 429 ||
        status === 500 ||
        status === 502 ||
        status === 503 ||
        status === 504
      );
    };

    const getBackendErrorMessage = (error: unknown): string | null => {
      if (!axios.isAxiosError(error)) return null;

      const axiosError = error as any;
      const data = axiosError.response?.data as any;

      if (typeof data?.message === "string" && data.message.trim()) {
        return data.message.trim();
      }

      return null;
    };

    try {
      setLoading(true);
      setEvents([]);
      setLoadFailureMessage("");
      setTotalEventCount(null);

      const rangeStart = fromDate.getTime();
      const rangeEnd = toDate.getTime();

      type TimeRange = { start: number; end: number };

      /*
       * IMPORTANT: do not fan a historical search out by pump/source.
       * A month of alarms can contain thousands of sources, which is what caused
       * 13k+ browser requests and 50+ minute loads. Six-hour all-source ranges
       * keep the request count bounded by time instead:
       *   1 day  = 4 requests
       *   30 days = ~120 requests
       *   90 days = ~360 requests
       * with only four requests in flight at once.
       */
      const rootRanges: TimeRange[] = [];
      for (let start = rangeStart; start < rangeEnd; start += RANGE_CHUNK_MS) {
        rootRanges.push({
          start,
          end: Math.min(rangeEnd, start + RANGE_CHUNK_MS),
        });
      }

      if (rootRanges.length === 0) {
        setEvents([]);
        setTotalEventCount(0);
        setLoadFailureMessage("");
        return;
      }

      const eventMap = new Map<string, ReengagementEvent>();
      let nextRangeIndex = 0;
      let completedRanges = 0;
      let failedRanges = 0;
      let adaptiveSplitCount = 0;
      let retryCount = 0;
      let lastProgressUpdate = 0;

      const eventKey = (event: ReengagementEvent) =>
        [
          event.fleet,
          event.pump,
          event.neutral_time_ms,
          event.reengagement_time_ms,
        ].join("-");

      const mergeEvents = (rawEvents: ReengagementEvent[]) => {
        rawEvents.map(normalizeEvent).forEach((event) => {
          const key = eventKey(event);
          const existing = eventMap.get(key);

          if (!existing) {
            eventMap.set(key, event);
            return;
          }

          const candidateGap =
            event.alarm_time_ms > 0 &&
            event.alarm_time_ms <= event.neutral_time_ms
              ? event.neutral_time_ms - event.alarm_time_ms
              : Number.MAX_SAFE_INTEGER;

          const existingGap =
            existing.alarm_time_ms > 0 &&
            existing.alarm_time_ms <= existing.neutral_time_ms
              ? existing.neutral_time_ms - existing.alarm_time_ms
              : Number.MAX_SAFE_INTEGER;

          if (
            candidateGap < existingGap ||
            (candidateGap === existingGap &&
              event.alarm_time_ms > existing.alarm_time_ms)
          ) {
            eventMap.set(key, event);
          }
        });
      };

      const currentSortedEvents = () =>
        Array.from(eventMap.values()).sort(
          (left, right) =>
            Number(right.reengagement_time_ms) -
            Number(left.reengagement_time_ms),
        );

      const publishProgress = (force = false) => {
        if (!isCurrentLoad()) return;

        const now = Date.now();
        if (!force && now - lastProgressUpdate < PROGRESS_UPDATE_INTERVAL_MS) {
          return;
        }
        lastProgressUpdate = now;

        const currentEvents = currentSortedEvents();
        setEvents(currentEvents);
        setLoadingProgress(
          `Loading historical events • ${completedRanges} of ${rootRanges.length} time blocks complete` +
            ` • ${currentEvents.length.toLocaleString()} event${currentEvents.length === 1 ? "" : "s"} found` +
            (retryCount > 0
              ? ` • ${retryCount} retr${retryCount === 1 ? "y" : "ies"}`
              : "") +
            (adaptiveSplitCount > 0
              ? ` • ${adaptiveSplitCount} slow block${adaptiveSplitCount === 1 ? "" : "s"} split`
              : ""),
        );
      };

      const sleep = (milliseconds: number) =>
        new Promise((resolve) => window.setTimeout(resolve, milliseconds));

      const statusOf = (error: unknown) =>
        axios.isAxiosError(error) ? Number((error as any).response?.status) : 0;

      const requestRange = async (range: TimeRange) => {
        const response = await axios.get(
          `${backendUrl}/pump-reengagement-events`,
          {
            ...requestConfig,
            // Deliberately disable Axios' client timeout. Killing a SQL request
            // in the browser does not stop the server-side query and previously
            // caused replacement/split requests to pile up behind it.
            timeout: REQUEST_TIMEOUT_MS,
            signal: controller.signal,
            params: {
              start_time: range.start,
              end_time: range.end,
              return_all: 1,
              min_rpm: minimumRpm ?? undefined,
            },
          },
        );

        return Array.isArray(response.data?.events)
          ? (response.data.events as ReengagementEvent[])
          : Array.isArray(response.data)
            ? (response.data as ReengagementEvent[])
            : [];
      };

      const loadRangeAdaptive = async (
        range: TimeRange,
        depth = 0,
      ): Promise<ReengagementEvent[]> => {
        let lastError: unknown = null;

        for (let attempt = 0; attempt <= MAX_RATE_LIMIT_RETRIES; attempt += 1) {
          try {
            return await requestRange(range);
          } catch (error) {
            if (controller.signal.aborted || axios.isCancel(error)) {
              throw error;
            }

            lastError = error;
            const status = statusOf(error);

            // 429 means the server is busy. Retry the same bounded range with
            // exponential backoff; never split it and multiply request volume.
            if (status === 429 && attempt < MAX_RATE_LIMIT_RETRIES) {
              retryCount += 1;
              publishProgress(true);
              await sleep(RETRY_BASE_DELAY_MS * Math.pow(2, attempt));
              continue;
            }

            break;
          }
        }

        // For real transient server/network failures, retry the exact same
        // six-hour block twice before considering a split.
        if (isTransientRangeError(lastError) && statusOf(lastError) !== 429) {
          for (let retry = 0; retry < MAX_TRANSIENT_RETRIES; retry += 1) {
            retryCount += 1;
            publishProgress(true);
            await sleep(RETRY_BASE_DELAY_MS * (retry + 1));

            try {
              return await requestRange(range);
            } catch (error) {
              if (controller.signal.aborted || axios.isCancel(error)) {
                throw error;
              }
              lastError = error;
            }
          }
        }

        const duration = range.end - range.start;
        if (
          isTransientRangeError(lastError) &&
          statusOf(lastError) !== 429 &&
          duration > MIN_SPLIT_RANGE_MS
        ) {
          const midpoint = range.start + Math.floor(duration / 2);

          if (midpoint > range.start && midpoint < range.end) {
            adaptiveSplitCount += 1;
            publishProgress(true);

            // Keep split recovery sequential inside the current worker. A slow
            // range therefore never increases database concurrency.
            const left = await loadRangeAdaptive(
              { start: range.start, end: midpoint },
              depth + 1,
            );
            const right = await loadRangeAdaptive(
              { start: midpoint, end: range.end },
              depth + 1,
            );

            return [...left, ...right];
          }
        }

        throw (
          lastError ??
          new Error(`Unable to load re-engagement range at depth ${depth}`)
        );
      };

      publishProgress(true);

      const worker = async () => {
        while (isCurrentLoad()) {
          const rangeIndex = nextRangeIndex;
          nextRangeIndex += 1;

          if (rangeIndex >= rootRanges.length) return;

          try {
            const rangeEvents = await loadRangeAdaptive(rootRanges[rangeIndex]);
            mergeEvents(rangeEvents);
          } catch (error) {
            if (controller.signal.aborted || axios.isCancel(error)) return;

            // Configuration/data-shape errors should stop immediately. Retrying
            // or splitting a bad request cannot make it valid.
            if (!isTransientRangeError(error)) {
              throw error;
            }

            failedRanges += 1;
            console.error(
              `Failed historical re-engagement block ${rangeIndex + 1}/${rootRanges.length}:`,
              error,
            );
          } finally {
            if (isCurrentLoad()) {
              completedRanges += 1;
              publishProgress();
            }
          }
        }
      };

      const workerCount = Math.min(
        MAX_RANGE_CONCURRENT_REQUESTS,
        rootRanges.length,
      );

      await Promise.all(Array.from({ length: workerCount }, () => worker()));

      if (!isCurrentLoad()) return;

      const finalEvents = currentSortedEvents();
      setEvents(finalEvents);

      if (failedRanges > 0) {
        setTotalEventCount(null);
        const failureMessage =
          `${failedRanges} bounded time block${failedRanges === 1 ? "" : "s"} could not be completed after retry/split recovery. ` +
          `${finalEvents.length.toLocaleString()} events are shown from completed blocks.`;
        setLoadFailureMessage(failureMessage);
        toast.error(failureMessage);
        return;
      }

      setTotalEventCount(finalEvents.length);
      setLoadFailureMessage("");
    } catch (error) {
      if (controller.signal.aborted || axios.isCancel(error)) {
        return;
      }

      console.error("Failed to load re-engagement events:", error);

      const failureMessage =
        getBackendErrorMessage(error) ||
        "Failed to load flagged re-engagement events. Please retry.";

      setLoadFailureMessage(failureMessage);
      toast.error(failureMessage);
      setEvents([]);
      setTotalEventCount(null);
    } finally {
      if (loadGenerationRef.current === loadGeneration) {
        setLoading(false);
        setLoadingProgress("");

        if (activeLoadControllerRef.current === controller) {
          activeLoadControllerRef.current = null;
        }
      }
    }
  }, [backendUrl, fromDate, toDate, minimumRpmFilter]);

  /*
   * Local development runs React StrictMode, which mounts effects twice.
   * Schedule the initial load for the next tick so the first StrictMode
   * setup/cleanup cycle can cancel the timer before any request is sent.
   * The second setup then performs the real initial load once.
   */
  useEffect(() => {
    const initialLoadTimer = window.setTimeout(() => {
      void loadEvents();
    }, 0);

    return () => {
      window.clearTimeout(initialLoadTimer);
    };
    // Initial page load only. Date changes are applied when the user clicks Search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      activeLoadControllerRef.current?.abort();
    };
  }, []);

  const fleetOptions = useMemo(() => {
    return Array.from(new Set(events.map((event) => event.fleet))).sort(
      (left, right) => left - right,
    );
  }, [events]);

  const filteredEvents = useMemo(() => {
    const selectedPump = pumpFilter.trim().toLowerCase();
    const generalSearch = searchText.trim().toLowerCase();

    return events.filter((event) => {
      if (fleetFilter && String(event.fleet) !== fleetFilter) {
        return false;
      }

      if (
        selectedPump &&
        !String(event.pump).toLowerCase().includes(selectedPump)
      ) {
        return false;
      }

      if (!generalSearch) {
        return true;
      }

      return (
        String(event.fleet).includes(generalSearch) ||
        String(event.pump).toLowerCase().includes(generalSearch) ||
        String(event.alarm).toLowerCase().includes(generalSearch)
      );
    });
  }, [events, fleetFilter, pumpFilter, searchText]);

  const formatDate = (timestamp?: number) => {
    if (!timestamp) {
      return "-";
    }

    return new Date(timestamp).toLocaleString();
  };

  const formatDelay = (seconds: number) => {
    const safeSeconds = Math.max(0, Number(seconds) || 0);
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = Math.floor(safeSeconds % 60);

    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatAlarm = (alarm: string) => {
    const rawAlarm = String(alarm || "").trim();
    const normalizedAlarm = rawAlarm.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

    // Friendly display for the exact source alarm used by this workflow.
    // Do not collapse other alarm variants into this label.
    if (normalizedAlarm === "transmissionoilpressurelowcriticalalarm") {
      return "Transmission Oil Pressure Low Critical";
    }

    return rawAlarm
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_:-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^./, (character) => character.toUpperCase());
  };

  const normalizeAlarmSeverity = (alarm: any): "critical" | "warning" => {
    const severity = String(alarm?.severity || "").toLowerCase();
    if (severity.includes("critical")) return "critical";
    return "warning";
  };

  const getAlarmChannel = (alarmName: string): string | null => {
    const normalizedAlarm = String(alarmName || "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toLowerCase();

    if (
      normalizedAlarm.includes("transmissionoilpressure") ||
      normalizedAlarm.includes("transmissionoilpress")
    ) {
      return "trans_oil_pressure";
    }
    if (normalizedAlarm.includes("engineoilpressure")) {
      return "engine_oil_pressure";
    }
    if (normalizedAlarm.includes("suctionpressure")) {
      return "suction_pressure";
    }
    if (
      normalizedAlarm.includes("transmissionoiltemperature") ||
      normalizedAlarm.includes("transmissionoiltemp")
    ) {
      return "trans_oil_temperature";
    }
    if (
      normalizedAlarm.includes("engineoiltemperature") ||
      normalizedAlarm.includes("engineoiltemp")
    ) {
      return "engine_oil_temperature";
    }
    if (
      normalizedAlarm.includes("enginecoolanttemperature") ||
      normalizedAlarm.includes("enginecoolanttemp")
    ) {
      return "engine_coolant_temperature";
    }
    if (
      normalizedAlarm.includes("powerendoilpressure") ||
      normalizedAlarm.includes("powerendlubepressure") ||
      normalizedAlarm.includes("powerendpressure")
    ) {
      return "power_end_oil_pressure";
    }
    if (
      normalizedAlarm.includes("powerendoiltemperature") ||
      normalizedAlarm.includes("powerendoiltemp")
    ) {
      return "power_end_oil_temperature";
    }
    return null;
  };

  const formatLocalDateTimeForApi = (date: Date) => {
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const handleViewChart = (event: ReengagementEvent) => {
    if (!event.alarm_time_ms || !event.reengagement_time_ms) {
      toast.warning("This event does not have a valid chart time range");
      return;
    }

    const chartStart =
      event.alarm_time_ms - CHART_MINUTES_BEFORE_ALARM * 60 * 1000;
    const chartEnd =
      event.reengagement_time_ms + CHART_MINUTES_AFTER_REENGAGEMENT * 60 * 1000;

    /*
     * Do not resolve alarm-derived channels on this page.
     *
     * Fleet is the single source of truth:
     *   1. call the existing /pump-alarms endpoint for the exact pump/window;
     *   2. exact-deduplicate the returned alarms;
     *   3. derive the unique Critical alarm channels;
     *   4. replace the chart selection before loading data.
     *
     * Sending only the permanent base channels here prevents a preliminary
     * request from leaking an extra channel into the final chart.
     */
    const params = new URLSearchParams({
      fleet: String(event.fleet),
      pump: String(event.pump),
      start_time: String(chartStart),
      end_time: String(chartEnd),
      channels: "gear,rpm",
      flagged_alarm: String(event.alarm || ""),
      flagged_graph_channel: String(event.graph_channel || ""),
      flagged_alarm_time: String(event.alarm_time_ms || ""),
      flagged_source_alarm_time:
        event.source_alarm_time_ms != null &&
        Number.isFinite(Number(event.source_alarm_time_ms))
          ? String(event.source_alarm_time_ms)
          : String(event.alarm_time_ms || ""),
      flagged_alarm_severity: "Critical",
      flagged_reengagement_view: "1",
      alarm_rpm:
        event.alarm_rpm != null && Number.isFinite(Number(event.alarm_rpm))
          ? String(event.alarm_rpm)
          : "",
      minimum_rpm: minimumRpmFilter.trim(),
    });

    const chartUrl = `/fleet?${params.toString()}`;
    window.open(chartUrl, "_blank", "noopener,noreferrer");
  };

  const escapeCsvValue = (value: unknown) => {
    const text = String(value ?? "");

    if (
      text.includes(",") ||
      text.includes('"') ||
      text.includes("\n") ||
      text.includes("\r")
    ) {
      return `"${text.replace(/"/g, '""')}"`;
    }

    return text;
  };

  const handleExport = () => {
    if (filteredEvents.length === 0) {
      toast.warning("No flagged re-engagement events available to export");
      return;
    }

    const headers = [
      "Fleet",
      "Pump",
      "Alarm",
      "Alarm Time",
      "Neutral Time",
      "Re-engagement Time",
      "Time to Re-engage",
      "Gear",
      "RPM at Alarm",
      "Discharge Pressure (PSI)",
    ];

    const rows = filteredEvents.map((event) => [
      event.fleet,
      event.pump,
      formatAlarm(event.alarm),
      formatDate(event.alarm_time_ms),
      formatDate(event.neutral_time_ms),
      formatDate(event.reengagement_time_ms),
      formatDelay(event.time_to_reengage_seconds),
      event.gear,
      event.alarm_rpm ?? "",
      Number(event.discharge_pressure) || 0,
    ]);

    const csvContent = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\r\n");

    /*
     * The BOM helps Excel correctly recognize UTF-8 CSV content.
     */
    const blob = new Blob([`\uFEFF${csvContent}`], {
      type: "text/csv;charset=utf-8;",
    });

    const downloadUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");

    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, 19);

    downloadLink.href = downloadUrl;
    downloadLink.download = `flagged-reengagement-events-${timestamp}.csv`;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    URL.revokeObjectURL(downloadUrl);

    toast.success(
      `Exported ${filteredEvents.length} flagged re-engagement event${
        filteredEvents.length === 1 ? "" : "s"
      }`,
    );
  };

  const clearFilters = () => {
    setFleetFilter("");
    setPumpFilter("");
    setSearchText("");
    setMinimumRpmFilter("");
    setFromDate(getTodayStart());
    setToDate(new Date());
  };

  return (
    <section
      className={`${styles.section} ${
        theme === "dark" ? "dark-theme" : "light-theme"
      }`}
    >
      <div className={styles.profile}>
        <Header
          title="Flagged Re-engagement Events"
          setShowSidebar={setShowSidebar}
          showSidebar={showSidebar}
        />

        <div className={styles.container}>
          <div
            className={`${styles.sidebarStyle} app-sidebar-rail`}
            style={{
              width: showSidebar ? "224px" : "56px",
              transition: "width 0.3s",
            }}
          >
            <Sidebar />
          </div>

          <div className={styles.mainContentStyle}>
            <div className={styles.interlocksSearchCard}>
              <div className={styles.dashboardHeader}>
                <p>
                  View flagged re-engagement events across all historical
                  fleets.
                </p>
              </div>

              <div className={styles.interlocksFilterGrid}>
                <select
                  value={fleetFilter}
                  onChange={(event) => setFleetFilter(event.target.value)}
                  className={styles.interlocksFilterInput}
                >
                  <option value="">All Fleets</option>

                  {fleetOptions.map((fleet) => (
                    <option key={fleet} value={fleet}>
                      Fleet {fleet}
                    </option>
                  ))}
                </select>

                <input
                  value={pumpFilter}
                  onChange={(event) => setPumpFilter(event.target.value)}
                  placeholder="Pump Number"
                  className={styles.interlocksFilterInput}
                />

                <input
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder="Search fleet, pump, or alarm"
                  className={styles.interlocksFilterInput}
                />

                <input
                  type="number"
                  min={0}
                  step={50}
                  value={minimumRpmFilter}
                  onChange={(event) => setMinimumRpmFilter(event.target.value)}
                  placeholder="Minimum Alarm RPM"
                  title="Show only events where RPM at the alarm time meets this minimum; leave blank to show all events"
                  className={styles.interlocksFilterInput}
                />

                <div className={styles.fleetDateInputWrap}>
                  <DatePicker
                    selected={fromDate}
                    onChange={(date) => date && setFromDate(date)}
                    showTimeSelect
                    timeIntervals={15}
                    dateFormat="MMM d, yyyy h:mm aa"
                    className={styles.fleetDateTextInput}
                    placeholderText="From Date"
                  />
                </div>

                <div className={styles.fleetDateInputWrap}>
                  <DatePicker
                    selected={toDate}
                    onChange={(date) => date && setToDate(date)}
                    showTimeSelect
                    timeIntervals={15}
                    dateFormat="MMM d, yyyy h:mm aa"
                    className={styles.fleetDateTextInput}
                    placeholderText="To Date"
                  />
                </div>

                <button
                  type="button"
                  className={styles.interlocksSearchBtn}
                  onClick={() => void loadEvents()}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Search"}
                </button>

                <button
                  type="button"
                  className={styles.interlocksClearBtn}
                  onClick={clearFilters}
                  disabled={loading}
                >
                  Clear
                </button>
              </div>
            </div>

            <div
              className={styles.tableToolbar}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "16px",
                width: "100%",
              }}
            >
              <span
                style={{
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {loading
                  ? totalEventCount === null
                    ? "Calculating total events..."
                    : `${events.length.toLocaleString()} of ${totalEventCount.toLocaleString()} events loaded`
                  : loadFailureMessage && totalEventCount === null
                    ? "Total unavailable"
                    : filteredEvents.length === events.length
                      ? `Total: ${(totalEventCount ?? events.length).toLocaleString()} flagged re-engagement event${(totalEventCount ?? events.length) === 1 ? "" : "s"}`
                      : `Showing ${filteredEvents.length.toLocaleString()} of ${(totalEventCount ?? events.length).toLocaleString()} total events`}
              </span>

              <button
                type="button"
                className={styles.interlocksSearchBtn}
                style={{
                  width: "auto",
                  minWidth: "130px",
                  flex: "0 0 auto",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                }}
                onClick={handleExport}
                disabled={loading || filteredEvents.length === 0}
              >
                Export CSV
              </button>
            </div>

            <div className={styles.tableContainer}>
              <div style={{ overflowX: "auto" }}>
                <table className={styles.table} style={{ minWidth: "1500px" }}>
                  <thead>
                    <tr>
                      <th className={styles.tableHeader}>Fleet</th>
                      <th className={styles.tableHeader}>Pump</th>
                      <th className={styles.tableHeader}>Alarm</th>
                      <th className={styles.tableHeader}>Alarm Time</th>
                      <th className={styles.tableHeader}>Neutral Time</th>
                      <th className={styles.tableHeader}>Re-engagement Time</th>
                      <th className={styles.tableHeader}>Time to Re-engage</th>
                      <th className={styles.tableHeader}>Gear</th>
                      <th className={styles.tableHeader}>RPM at Alarm</th>
                      <th className={styles.tableHeader}>Discharge Pressure</th>
                      <th className={styles.tableHeader}>Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading && filteredEvents.length === 0 && (
                      <tr>
                        <td colSpan={11} className={styles.noDataTable}>
                          {loadingProgress || "Loading flagged events..."}
                        </td>
                      </tr>
                    )}

                    {!loading &&
                      loadFailureMessage &&
                      filteredEvents.length > 0 && (
                        <tr>
                          <td colSpan={11} className={styles.noDataTable}>
                            {loadFailureMessage}
                          </td>
                        </tr>
                      )}

                    {!loading && filteredEvents.length === 0 ? (
                      <tr>
                        <td colSpan={11} className={styles.noDataTable}>
                          {loadFailureMessage ||
                            "No flagged re-engagement events match the selected filters."}
                        </td>
                      </tr>
                    ) : (
                      filteredEvents.map((event) => (
                        <tr
                          key={[
                            event.fleet,
                            event.pump,
                            event.alarm,
                            event.alarm_time_ms,
                            event.neutral_time_ms,
                            event.reengagement_time_ms,
                          ].join("-")}
                          className={styles.tableRow}
                        >
                          <td className={styles.tableCell}>{event.fleet}</td>

                          <td className={styles.tableCell}>{event.pump}</td>

                          <td className={styles.tableCell}>
                            {formatAlarm(event.alarm)}
                          </td>

                          <td
                            className={styles.tableCell}
                            title={
                              event.alarm_time_adjusted &&
                              event.source_alarm_time_ms != null
                                ? `Trip Pressure trigger time recovered from telemetry. Source alarm row: ${formatDate(
                                    event.source_alarm_time_ms,
                                  )}`
                                : undefined
                            }
                          >
                            {formatDate(event.alarm_time_ms)}
                          </td>

                          <td className={styles.tableCell}>
                            {formatDate(event.neutral_time_ms)}
                          </td>

                          <td className={styles.tableCell}>
                            {formatDate(event.reengagement_time_ms)}
                          </td>

                          <td className={styles.tableCell}>
                            {formatDelay(event.time_to_reengage_seconds)}
                          </td>

                          <td className={styles.tableCell}>{event.gear}</td>

                          <td className={styles.tableCell}>
                            {event.alarm_rpm === null ||
                            event.alarm_rpm === undefined
                              ? "-"
                              : Number(event.alarm_rpm).toLocaleString()}
                          </td>

                          <td className={styles.tableCell}>
                            {Number(event.discharge_pressure).toLocaleString()}{" "}
                            PSI
                          </td>

                          <td className={styles.tableCell}>
                            <button
                              type="button"
                              className={styles.interlocksSearchBtn}
                              onClick={() => handleViewChart(event)}
                            >
                              View Chart
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FlaggedReengagementEvents;
